import { FilterOption } from '#service/autocomplete-service/option';

const formatEventTypeLabel = key =>
  key
    .split('_')
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');

class ActionTypeFilterOptionService {
  #eventTypes;

  #eventGroups;

  #labels;

  constructor(searchFields, store, options) {
    if (!options?.eventTypes && !options?.eventGroups?.length) {
      throw new Error('Event types are not provided');
    }

    this.#eventTypes = options.eventTypes ?? {};
    this.#eventGroups = options.eventGroups ?? [];
    this.#labels = options?.labels;
  }

  getLabel(key, id) {
    return this.#labels?.[key] ?? this.#labels?.[id] ?? formatEventTypeLabel(key);
  }

  buildGroupOption({ key, label, eventIds }) {
    const ids = eventIds.map(eventId => String(eventId));

    return {
      label,
      key,
      eventIds,
      value: ids.join(','),
      id: ids.join(',')
    };
  }

  getOptionsRaw() {
    const singleOptions = Object.entries(this.#eventTypes).map(([key, id]) => {
      const value = String(id);

      return {
        label: this.getLabel(key, value),
        value,
        id: value
      };
    });

    const groupOptions = this.#eventGroups.map(group => this.buildGroupOption(group));

    return [...singleOptions, ...groupOptions];
  }

  matchesIds(option, idsArray) {
    if (option.eventIds?.length) {
      return option.eventIds.some(eventId => idsArray.includes(String(eventId)));
    }

    return idsArray.includes(option.id);
  }

  filterBySearch(options, value) {
    if (!value) {
      return options;
    }

    const query = value.toLowerCase();

    return options.filter(option => option.label.toLowerCase().includes(query));
  }

  async fetchOptionsRaw(value, fetchOptions, ids) {
    let options = this.getOptionsRaw();

    if (value) {
      options = this.filterBySearch(options, value);
    }

    if (ids) {
      const idsArray = ids.split(',');

      return options.filter(option => this.matchesIds(option, idsArray));
    }

    return options;
  }

  async getOptions(value, fetchOptions, ids) {
    const fetchedOptions = await this.fetchOptionsRaw(value, fetchOptions, ids);

    return this.transformOptions(fetchedOptions);
  }

  transformOptions(options) {
    return options.map(option => new FilterOption(option, option.label));
  }
}

export default ActionTypeFilterOptionService;
